import type { Line } from "../render/Line";
import type { CsClient } from "../net/csClient";

const MAX_FINGERS = 10;
// Fallback only. The real value is pushed by the server (SET_UV_HZ) on connect,
// so the operator can raise or lower the whole fleet mid-show; this is just what
// a phone uses in the moments before that frame lands.
const DEFAULT_UV_HZ = 20;

// There used to be a UV_THRESHOLD = 0.01 dead-zone here, and a per-move time
// gate alongside it. Both are gone — see armUvSampler() for the measurements.
// Short version: the dead-zone was applied against the last SENT position, so a
// finger moving slower than 0.01 per tick had its motion thrown away rather than
// accumulated, and the packet it saved was sent anyway by the keepalive carrying
// the stale position.
//
// A skip does live in the client again, but it is a different animal and the
// difference is the whole point: it compares the QUANTISED pair, in
// CsClient.sendFingerUV, so the only frame it can ever suppress is one that
// would have been byte-for-byte identical on the wire. Motion slower than the
// threshold was the old one's blind spot; this one has no threshold to be blind
// below. A finger held perfectly still now costs nothing instead of twenty
// packets a second, and nothing downstream can tell the difference, because
// there is none to tell.

interface FingerState {
  pointerId: number;
  fingerIdx: number;
  currentLine: Line | null;
  hasEverTouchedLine: boolean;
  // Pad mode: whether this touch currently has a note on, and the line index the
  // note-on carried (reused at note-off so the pair matches for the audio engine).
  padOn: boolean;
  noteLine: number;
  // LATEST known position for this finger, not the last one sent. The sampler
  // reads it every tick, so a move only has to write here; -1 means "no sample
  // yet", which is what makes the first UV of a note go out immediately.
  lastU: number;
  lastV: number;
}

export type InteractionMode = "line" | "pad";

export interface VisualHooks {
  onLineHit?: (line: Line, fingerY: number) => void;
  onLineMove?: (line: Line, fingerY: number) => void;
  onLineRelease?: (line: Line) => void;
}

export class TouchTracker {
  private stageEl: HTMLElement;
  private lines: Line[];
  private client: CsClient;
  private width: number;
  private height: number;
  private hooks: VisualHooks;
  private mode: InteractionMode;

  private byPointerId = new Map<number, FingerState>();
  private fingerSlots = new Array<boolean>(MAX_FINGERS).fill(false);
  // The operator's cap, which is what actually decides how many slots get handed
  // out. MAX_FINGERS above stays the pool's physical ceiling; this is the line
  // drawn inside it, and it starts wide open because the server pushes the real
  // number on connect (SET_MAX_FINGERS) and refusing touches before that frame
  // lands would break a phone that reconnects mid-song.
  private maxFingers = MAX_FINGERS;
  private uvRateMs = 1000 / DEFAULT_UV_HZ;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  // How many sampler ticks share one packet. 1 until the server says otherwise.
  private uvPack = 1;
  // Monotonic sampler tick. It is both the flush clock and the age reference the
  // client stamps into each frame, so it must never restart while a socket is up.
  private uvTick = 0;

  sentCount = 0;
  droppedCount = 0;
  totalCount = 0;

  constructor(opts: {
    stageEl: HTMLElement;
    lines: Line[];
    client: CsClient;
    width: number;
    height: number;
    hooks?: VisualHooks;
    mode?: InteractionMode;
  }) {
    this.stageEl = opts.stageEl;
    this.lines = opts.lines;
    this.client = opts.client;
    this.width = opts.width;
    this.height = opts.height;
    this.hooks = opts.hooks ?? {};
    this.mode = opts.mode ?? "line";
    this.attach();
  }

  // Switch interaction mode (server-driven). Any in-flight gesture is released in
  // the current mode first so no note is left hanging; fingers still physically
  // down are forgotten until re-pressed.
  setMode(mode: InteractionMode) {
    if ((mode !== "line" && mode !== "pad") || mode === this.mode) return;
    for (const state of this.byPointerId.values()) {
      this.releaseGesture(state);
      this.freeSlot(state.fingerIdx);
    }
    this.byPointerId.clear();
    this.mode = mode;
  }

  /**
   * End every gesture in flight, as if each finger had lifted.
   *
   * For the moments when the stage stops being touchable while fingers are on
   * it — the phone turned to portrait, say. An overlay stops the NEXT touch;
   * the note already sounding in the hall is this method's business, and
   * nothing downstream can end it on its own.
   */
  releaseAll() {
    for (const state of this.byPointerId.values()) {
      this.releaseGesture(state);
      this.freeSlot(state.fingerIdx);
    }
    this.byPointerId.clear();
  }

  setStageSize(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  setLines(lines: Line[]) {
    for (const state of this.byPointerId.values()) {
      // Release any held note (mode-aware) + visual holder before the lines it
      // referenced disappear; the finger re-arms on its next move.
      this.releaseGesture(state);
      state.hasEverTouchedLine = false;
      state.lastU = -1;
    }
    this.lines = lines;
  }

  private attach() {
    const el = this.stageEl;
    el.style.touchAction = "none";
    el.addEventListener("pointerdown", this.onDown);
    el.addEventListener("pointermove", this.onMove);
    el.addEventListener("pointerup", this.onUp);
    el.addEventListener("pointercancel", this.onUp);

    this.armUvSampler();
  }

  /**
   * The single UV transmitter: every tick, send each active finger's LATEST
   * position. Pointer events only write that position; they never send.
   *
   * This used to be two paths racing on one `lastSentMs` budget — a keepalive
   * (for a finger held still, which fires no pointermove) that resent the old
   * position, and a per-move send gated on `now - lastSentMs >= uvRateMs` plus a
   * 0.01 dead-zone. The keepalive ticks at exactly uvRateMs while pointermove
   * arrives at 40-120 Hz, so the keepalive almost always claimed the budget
   * first and the fresh position was discarded as "too soon".
   *
   * Measured on a real phone playing: 18.2 packets/s reaching the cloud, of
   * which only 4.5/s carried a position different from the one before. Three
   * quarters of the audience's bandwidth was spent retransmitting stale
   * coordinates, and every finger was effectively sampled at 4.5 Hz. Reproduced
   * headless with a synthetic drag at 42 Hz input: 14.6 packets/s, 7.7/s new,
   * 47% duplicates, and the packet interval stretched to 70 ms against a 50 ms
   * setting — the race cost rate as well as freshness.
   *
   * Sample-and-hold fixes both at once and costs nothing: the same number of
   * packets, each carrying the newest position. It also removes the dead-zone,
   * which was actively harmful — being measured against the last SENT position,
   * slow movement never accumulated past it and was simply lost.
   *
   * The period IS the UV rate, so changing the rate re-arms it; a plain variable
   * would leave the old period running.
   *
   * Sampling and SENDING are two clocks now. Every tick still takes a sample; the
   * packet leaves once every `uvPack` ticks, carrying whatever was taken since
   * the last one. Flushing on a tick COUNT rather than on a full buffer is what
   * makes the added delay exactly `uvPack × uvRateMs` and no more — with a
   * full-buffer rule, a finger the dedup has gone quiet on could leave a position
   * sitting in the buffer until it happened to move again.
   */
  private armUvSampler(): void {
    if (this.keepaliveTimer !== null) clearInterval(this.keepaliveTimer);
    this.keepaliveTimer = setInterval(() => {
      const tick = ++this.uvTick;
      for (const s of this.byPointerId.values()) {
        const active = this.mode === "pad" ? s.padOn : s.currentLine != null;
        if (!active || s.lastU < 0) continue;
        // false means the client recognised this position as one it has already
        // put on the wire. That is a saved packet, not a sent one.
        if (this.client.sendFingerUV(s.fingerIdx, s.lastU, s.lastV, tick) === false) this.droppedCount++;
        else this.sentCount++;
        this.totalCount++;
      }
      if (tick % this.uvPack === 0) this.client.flushUvPack?.(tick);
    }, this.uvRateMs);
  }

  /** Operator-pushed send rate, in Hz (1–60). Clamped, since it comes off the wire. */
  setUvHz(hz: number): void {
    const clamped = Math.max(1, Math.min(60, Math.round(hz)));
    const next = 1000 / clamped;
    if (next === this.uvRateMs) return;
    this.uvRateMs = next;
    // Anything buffered was sampled on the old period and would otherwise wait out
    // a window it was never taken for — up to four ticks of a rate that no longer
    // exists.
    this.client.flushUvPack?.(this.uvTick);
    this.armUvSampler();
  }

  /**
   * Operator-pushed packet packing (1–4, 1 = off). Clamped, off the wire.
   *
   * This does not change how often a finger is sampled, only how often a packet
   * leaves — which is the number a mobile network actually runs out of. Every
   * position still arrives, up to `n × uvRateMs` later than it would have, each
   * carrying its own age so the server can put it back where it belongs in time.
   * Notes are never packed: the client sends whatever is buffered ahead of a
   * NOTE_ON or NOTE_OFF, so an attack is never waiting behind movement.
   */
  setUvPack(n: number): void {
    const next = Math.max(1, Math.min(4, Math.round(n)));
    if (next === this.uvPack) return;
    this.uvPack = next;
    this.client.flushUvPack?.(this.uvTick);
  }

  /**
   * Operator-pushed per-seat finger cap (1–10). Clamped, since it comes off the
   * wire, and never above the slot pool.
   *
   * Lowering it mid-gesture ends the fingers that no longer fit, exactly as if
   * they had lifted. Leaving them held would hold notes the server has already
   * stopped relaying — a ring on the glass with nothing sounding under it.
   */
  setMaxFingers(n: number): void {
    const next = Math.max(1, Math.min(MAX_FINGERS, Math.round(n)));
    if (next === this.maxFingers) return;
    this.maxFingers = next;
    for (const [pointerId, state] of [...this.byPointerId]) {
      if (state.fingerIdx >= next) {
        this.releaseGesture(state);
        this.freeSlot(state.fingerIdx);
        this.byPointerId.delete(pointerId);
      }
    }
  }

  private allocSlot(): number {
    for (let i = 0; i < this.maxFingers; i++) {
      if (!this.fingerSlots[i]) {
        this.fingerSlots[i] = true;
        return i;
      }
    }
    return -1;
  }

  private freeSlot(idx: number) {
    if (idx >= 0 && idx < MAX_FINGERS) this.fingerSlots[idx] = false;
  }

  private hitTest(x: number): Line | null {
    let best: Line | null = null;
    let bestDist = Infinity;
    for (const line of this.lines) {
      if (line.contains(x)) {
        const d = line.distanceTo(x);
        if (d < bestDist) {
          bestDist = d;
          best = line;
        }
      }
    }
    return best;
  }

  private stageCoords(ev: PointerEvent): { x: number; y: number } {
    const rect = this.stageEl.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  }

  private onDown = (ev: PointerEvent) => {
    if (this.byPointerId.has(ev.pointerId)) return;
    const slot = this.allocSlot();
    if (slot < 0) return;
    const { x, y } = this.stageCoords(ev);
    const state: FingerState = {
      pointerId: ev.pointerId,
      fingerIdx: slot,
      currentLine: null,
      hasEverTouchedLine: false,
      padOn: false,
      noteLine: 0,
      lastU: -1,
      lastV: -1,
    };
    this.byPointerId.set(ev.pointerId, state);
    try {
      (ev.target as Element)?.setPointerCapture?.(ev.pointerId);
    } catch {
      /* ignore */
    }
    if (this.mode === "pad") this.processPadMove(state, x, y);
    else this.processMove(state, x, y);
    ev.preventDefault();
  };

  private onMove = (ev: PointerEvent) => {
    const state = this.byPointerId.get(ev.pointerId);
    if (!state) return;
    const { x, y } = this.stageCoords(ev);
    if (this.mode === "pad") this.processPadMove(state, x, y);
    else this.processMove(state, x, y);
  };

  private onUp = (ev: PointerEvent) => {
    const state = this.byPointerId.get(ev.pointerId);
    if (!state) return;
    this.releaseGesture(state);
    this.freeSlot(state.fingerIdx);
    this.byPointerId.delete(ev.pointerId);
  };

  private processMove(state: FingerState, x: number, y: number) {
    const hit = this.hitTest(x);

    if (hit !== state.currentLine) {
      if (state.currentLine) {
        this.client.sendNoteOff(state.currentLine.index, state.fingerIdx);
        state.currentLine.removeHolder(state.fingerIdx);
        this.hooks.onLineRelease?.(state.currentLine);
        this.sentCount++;
        this.totalCount++;
      }
      state.currentLine = hit;
      if (hit) {
        // Send the attack position too: lines sit at their true wavelength
        // (irregular spacing), so the projection needs u,v to colour the ring
        // by wavelength rather than inferring it from the line index.
        const u = Math.max(0, Math.min(1, x / this.width));
        const v = Math.max(0, Math.min(1, 1 - y / this.height));
        this.client.sendNoteOn(hit.index, state.fingerIdx, u, v);
        hit.addHolder(state.fingerIdx, x, y);
        this.hooks.onLineHit?.(hit, y);
        state.hasEverTouchedLine = true;
        this.sentCount++;
        this.totalCount++;
      }
    } else if (hit) {
      hit.updateHolder(state.fingerIdx, x, y);
      this.hooks.onLineMove?.(hit, y);
    }

    if (!state.hasEverTouchedLine) return;

    const u = Math.max(0, Math.min(1, x / this.width));
    const v = Math.max(0, Math.min(1, 1 - y / this.height));
    // Record only. The sampler transmits. The very first sample of a note goes
    // out at once so the note does not start from a position nobody was told.
    const first = state.lastU < 0;
    state.lastU = u;
    state.lastV = v;
    if (first) {
      // No tick: out on its own, now. This is the note's opening position and it
      // must not wait for a packet to fill.
      if (this.client.sendFingerUV(state.fingerIdx, u, v) === false) this.droppedCount++;
      else this.sentCount++;
      this.totalCount++;
    } else {
      // Coalesced into the next tick rather than thrown away: the position it
      // carried is the one that will be sent.
      this.droppedCount++;
    }
  }

  // Pad mode: the whole screen is a seamless touchpad. The line visuals behave
  // exactly as in line mode (a line lights up / bows while a finger crosses it),
  // but the network layer is driven by touch presence, not line contact — a note
  // is on for the whole touch and u,v streams regardless of where the finger is.
  private processPadMove(state: FingerState, x: number, y: number) {
    // Visuals — identical to line mode's line-highlight/bow handling.
    const hit = this.hitTest(x);
    if (hit !== state.currentLine) {
      if (state.currentLine) {
        state.currentLine.removeHolder(state.fingerIdx);
        this.hooks.onLineRelease?.(state.currentLine);
      }
      state.currentLine = hit;
      if (hit) {
        hit.addHolder(state.fingerIdx, x, y);
        this.hooks.onLineHit?.(hit, y);
      }
    } else if (hit) {
      hit.updateHolder(state.fingerIdx, x, y);
      this.hooks.onLineMove?.(hit, y);
    }

    const u = Math.max(0, Math.min(1, x / this.width));
    const v = Math.max(0, Math.min(1, 1 - y / this.height));

    // Note-on by touch presence, not line contact: arm on the first sample of a
    // touch (or after a forced release), carrying the position so the ring
    // colours by wavelength. The line index is the nearest line, for the audio.
    if (!state.padOn) {
      state.noteLine = this.nearestLineIndex(x);
      this.client.sendNoteOn(state.noteLine, state.fingerIdx, u, v);
      state.padOn = true;
      state.lastU = -1; // force the first UV below
      this.sentCount++;
      this.totalCount++;
    }

    // Record only; the sampler transmits. See armUvSampler().
    const first = state.lastU < 0;
    state.lastU = u;
    state.lastV = v;
    if (first) {
      if (this.client.sendFingerUV(state.fingerIdx, u, v) === false) this.droppedCount++;
      else this.sentCount++;
      this.totalCount++;
    } else {
      this.droppedCount++;
    }
  }

  // Release a gesture's note (mode-aware) and its visual line holder. Safe to
  // call whether or not a note/holder is active. Does not free the finger slot.
  private releaseGesture(state: FingerState) {
    if (this.mode === "pad") {
      if (state.padOn) {
        this.client.sendNoteOff(state.noteLine, state.fingerIdx);
        state.padOn = false;
        this.sentCount++;
        this.totalCount++;
      }
    } else if (state.currentLine) {
      this.client.sendNoteOff(state.currentLine.index, state.fingerIdx);
      this.sentCount++;
      this.totalCount++;
    }
    if (state.currentLine) {
      state.currentLine.removeHolder(state.fingerIdx);
      this.hooks.onLineRelease?.(state.currentLine);
      state.currentLine = null;
    }
  }

  // Index of the line closest to x (no hit-band gate) — pad mode maps any touch
  // position to the nearest string for the audio note. Returns 0 if no lines.
  private nearestLineIndex(x: number): number {
    let best = 0;
    let bestDist = Infinity;
    for (const line of this.lines) {
      const d = line.distanceTo(x);
      if (d < bestDist) {
        bestDist = d;
        best = line.index;
      }
    }
    return best;
  }
}
